'use strict';

/**
 * T67 W3 — JIT decrypt-grant signing (browser-side) + pure crypto verification.
 *
 * Per PLAN-SEAMS-MULTIUSER-V1 LD-13 + CLAUDE.md "LEGAL FOUNDATION":
 *   HGE physically cannot decrypt user data without user-keyed cryptographic
 *   consent. A JIT-grant is a short-lived, single-use, EdDSA-signed token
 *   authorizing HGE to drive a browser-mediated decrypt for a specific user
 *   over a specific data path. Replay protection is achieved through the
 *   server-side jti cache (see auth/jit-grant-validator.js).
 *
 * Grant schema (LD-13) — FLAT structure (no nested body wrapper):
 *   {
 *     userId:    string,                 // user identifier
 *     dataPath:  string,                 // e.g. "var/seams/projects/abc.json"
 *     expiresAt: ISO-8601 string (UTC),  // default = now + 15min
 *     nonce:     32-hex-char string,     // 16 cryptographic bytes hex-encoded
 *     jti:       UUID-v4 string,         // unique grant ID, replay-detection key
 *     maxUses:   1,                      // hard-pinned in V1
 *     signature: base64 string,          // Ed25519 sig over canonical body bytes
 *     publicKey: base64 string,          // raw 32-byte Ed25519 public key
 *   }
 *
 * Signature:
 *   EdDSA (Ed25519) over the canonical byte-string of the grant body fields
 *   (the 6 LD-13 body fields, excluding signature + publicKey themselves).
 *
 * Canonical-encoding design (Opus self-audit):
 *   JSON.stringify is NOT canonical (key order, whitespace, type coercion,
 *   Unicode-normalization). Argon2id-parameter-drift bug class explicitly
 *   warned against in the W3 brief. Instead, this module uses a fixed-order
 *   length-prefixed UTF-8 byte concatenation with a domain-separator label.
 *   Tampering with any field changes the byte string → signature breaks.
 *
 * Module shape:
 *   - Pure functional. Does NOT track jti use (validator's responsibility).
 *   - Browser + server compatible (no Node-only APIs except crypto.randomUUID
 *     and crypto.randomBytes, both available in modern browsers via
 *     globalThis.crypto / Web Crypto API + node:crypto module).
 *   - Signature is over a canonical byte-string of the body. No JSON.
 *
 * Public key handling:
 *   The grant carries `publicKey` (raw Ed25519 public key, base64-encoded).
 *   The validator's job is to (a) verify the signature against the embedded
 *   publicKey, and (b) cross-check the embedded publicKey against the
 *   user's known identity public key (via injected publicKeyLookup). This
 *   prevents attacker-substituted keypairs.
 */

const { randomBytes, randomUUID } = require('node:crypto');
const { signWithIdentity, verifyIdentity, deriveIdentityKeypair } = require('./identity-key.js');

const TEXT_ENCODER = new TextEncoder();

// 15 minutes — matches LD-13 grant TTL.
const DEFAULT_EXPIRY_MS = 15 * 60 * 1000;

// Domain separator label — prevents cross-context signature reuse.
// Versioned so v2 changes don't accept v1 signatures and vice versa.
const SIGNING_DOMAIN_LABEL = 'oyster:jit-grant:v1\x00';

// UUID-v4 pattern (RFC 4122). Used for jti format validation.
const UUID_V4_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// ISO-8601 with required `Z` UTC suffix (per memory entry
// `feedback_jq_fromdateiso8601_requires_literal_Z_suffix`). expiresAt MUST be
// UTC `...Z` form (what `new Date().toISOString()` produces).
const ISO_8601_Z_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/;

const REQUIRED_BODY_FIELDS = ['userId', 'dataPath', 'expiresAt', 'nonce', 'jti', 'maxUses'];

// T365 P2 (LD-4/LD-8) — grant format versions.
//   GV_V1: the shipped Ed25519-only grant (no actor/aud). Still accepted (dual-accept).
//   GV_V2: adds the named `actor` (actor pubkey + label) + RFC-8707-style `aud`
//          (resource binding). The signed canonical encoding folds these in only
//          when present, behind an explicit presence byte, so a v1 grant and a v2
//          grant both verify deterministically against THEIR OWN encoding.
const GV_V1 = 'v1';
const GV_V2 = 'v2';

// T568 P8 (LD-9) — ACTION-BOUND grant verbs. DISTINCT (no hierarchy): a read
// grant can never authorize a write/delete and vice versa. Optional at this
// layer (presence-gated in the signed encoding); the remote-Control gate
// REQUIRES a verb on every grant it accepts.
const GRANT_VERBS = Object.freeze(['read', 'write', 'delete']);

// Domain label for the presentation-time PoP challenge (LD-3). Distinct from the
// grant-signing label so a PoP signature can never be replayed as a grant sig.
const POP_DOMAIN_LABEL = 'oyster:jit-grant-pop:v1\x00';

// T465 P6 (LD-53) — GV_V3 class: remote-desktop session scopes + consentMode.
// DISTINCT frame classes (no hierarchy): 'view' can never authorize 'input'.
// These ride INSIDE the signed canonical body (presence byte 0x03) so a
// mutated/added/stripped scope ALWAYS breaks the signature — the GOTCHA-137
// unsigned-scope escalation (view→input) is structurally impossible.
const RD_SCOPES = Object.freeze(['view', 'input', 'clipboard', 'file']);
const CONSENT_MODES = Object.freeze(['unattended', 'attended']);

/**
 * Encode the grant's body fields for signing using a fixed-order
 * length-prefixed byte concatenation with a domain-separator label.
 *
 * Layout (all length prefixes are 4-byte big-endian uint32):
 *   SIGNING_DOMAIN_LABEL                                    (UTF-8 bytes)
 *   uint32_BE(len(userId))    || userId                     (UTF-8 bytes)
 *   uint32_BE(len(dataPath))  || dataPath                   (UTF-8 bytes)
 *   uint32_BE(len(expiresAt)) || expiresAt                  (UTF-8 bytes)
 *   uint32_BE(len(nonce))     || nonce                      (UTF-8 bytes)
 *   uint32_BE(len(jti))       || jti                        (UTF-8 bytes)
 *   uint32_BE(maxUses)
 *
 * @param {object} grant — grant with all 6 required body fields at top level
 *                          (signature + publicKey are NOT included in signing
 *                          input — they sit alongside the body fields)
 * @returns {Uint8Array} canonical signing bytes
 */
function canonicalEncodeForSigning(grant) {
  for (const f of REQUIRED_BODY_FIELDS) {
    if (!Object.prototype.hasOwnProperty.call(grant, f)) {
      throw new Error(`canonicalEncodeForSigning: missing required field "${f}"`);
    }
  }
  const parts = [];
  parts.push(TEXT_ENCODER.encode(SIGNING_DOMAIN_LABEL));
  pushLengthPrefixedString(parts, grant.userId);
  pushLengthPrefixedString(parts, grant.dataPath);
  pushLengthPrefixedString(parts, grant.expiresAt);
  pushLengthPrefixedString(parts, grant.nonce);
  pushLengthPrefixedString(parts, grant.jti);
  // maxUses is an integer — encode as 4-byte big-endian uint32.
  const maxUsesBuf = new Uint8Array(4);
  new DataView(maxUsesBuf.buffer).setUint32(0, grant.maxUses >>> 0, false /* BE */);
  parts.push(maxUsesBuf);

  // T365 P2 (LD-4/LD-8) — version + optional actor/aud extension, presence-gated.
  // A single presence byte distinguishes v1 (0x00 — nothing follows) from v2
  // (0x01 — gv + aud + actor.publicKey + actor.label follow). This keeps the v1
  // byte-string IDENTICAL to the pre-extension encoding (so already-issued v1
  // grants verify unchanged) while making v2 unambiguous and tamper-evident.
  const hasExt = grant.gv === GV_V2 || grant.aud != null || grant.actor != null;
  if (!hasExt) {
    parts.push(new Uint8Array([0x00]));
  } else {
    parts.push(new Uint8Array([0x01]));
    pushLengthPrefixedString(parts, String(grant.gv || GV_V2));
    pushLengthPrefixedString(parts, grant.aud != null ? String(grant.aud) : '');
    const actor = grant.actor || {};
    pushLengthPrefixedString(parts, actor.publicKey != null ? String(actor.publicKey) : '');
    pushLengthPrefixedString(parts, actor.label != null ? String(actor.label) : '');
  }

  // T568 P8 (LD-9) — presence-gated ACTION-BOUND verb block. Same doctrine as
  // the v2 extension: a verb-LESS grant's byte-string stays IDENTICAL to the
  // pre-extension encoding (already-issued grants verify unchanged); a
  // verb-carrying grant appends 0x02 + the verb, so mutating, adding, or
  // stripping the verb ALWAYS breaks the signature (read can never be
  // escalated to write/delete post-signing).
  if (grant.verb != null) {
    parts.push(new Uint8Array([0x02]));
    pushLengthPrefixedString(parts, String(grant.verb));
  }

  // T465 P6 (LD-53) — presence-gated scope/consentMode block (0x03). Same
  // doctrine as 0x01/0x02: a scope-less grant's byte-string stays IDENTICAL
  // to the pre-extension encoding; a scoped grant folds a COUNT-prefixed
  // scope list + consentMode into the signed bytes, so mutating, adding,
  // re-ordering, or stripping either ALWAYS breaks the signature.
  if (grant.scopes != null || grant.consentMode != null) {
    parts.push(new Uint8Array([0x03]));
    const scopes = Array.isArray(grant.scopes) ? grant.scopes : [];
    pushLengthPrefixedString(parts, String(scopes.length));
    for (const s of scopes) pushLengthPrefixedString(parts, String(s));
    pushLengthPrefixedString(parts, grant.consentMode != null ? String(grant.consentMode) : '');
  }
  return concatUint8Arrays(parts);
}

function pushLengthPrefixedString(parts, str) {
  if (typeof str !== 'string') {
    throw new Error('canonicalEncodeForSigning: all string fields must be strings');
  }
  const bytes = TEXT_ENCODER.encode(str);
  const lenBuf = new Uint8Array(4);
  new DataView(lenBuf.buffer).setUint32(0, bytes.length, false /* BE */);
  parts.push(lenBuf);
  parts.push(bytes);
}

function concatUint8Arrays(arrays) {
  let total = 0;
  for (const a of arrays) total += a.length;
  const out = new Uint8Array(total);
  let off = 0;
  for (const a of arrays) {
    out.set(a, off);
    off += a.length;
  }
  return out;
}

function bytesToBase64(bytes) {
  // Node and browser-compatible: use Buffer when available, else btoa(String.fromCharCode...).
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(bytes).toString('base64');
  }
  /* c8 ignore next 4 */
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return globalThis.btoa(bin);
}

function base64ToBytes(b64) {
  if (typeof Buffer !== 'undefined') {
    return new Uint8Array(Buffer.from(b64, 'base64'));
  }
  /* c8 ignore next 6 */
  const bin = globalThis.atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/**
 * Generate a cryptographically random nonce as a 32-hex-char UUID string.
 * NOT using randomUUID() — to keep nonce visually distinct from jti and to
 * draw from a fresh 16-byte entropy pool. We render as hex, not UUID-v4
 * format, to make grep distinguishable.
 *
 * @returns {string} 32-hex-char string (16 random bytes)
 */
function generateNonce() {
  return Buffer.from(randomBytes(16)).toString('hex');
}

/**
 * Create a signed JIT-grant.
 *
 * @param {object} opts
 * @param {string} opts.userId    — user identifier
 * @param {string} opts.dataPath  — data path to authorize decrypt for
 * @param {string} [opts.expiresAt] — ISO-8601 UTC string; default now+15min
 * @param {Uint8Array} [opts.signingKey] — 32-byte Ed25519 private-key seed
 *                                          (from deriveIdentityKeypair).
 *                                          Optional in V1 for test/mock paths
 *                                          (signature + publicKey omitted).
 * @param {Uint8Array} [opts.masterKey] — alternative: derive signing key from
 *                                         32-byte master key at call-time
 *                                         (browser convenience).
 * @returns {Promise<object>} grant = { body, signature, publicKey }
 *
 * NOTE: createJitGrant is async to match the existing W0 governance test
 * contract (`await createJitGrant(...)`). The crypto is synchronous, but
 * the async signature lets us evolve to WebCrypto SubtleCrypto without
 * changing the call sites.
 */
async function createJitGrant({ userId, dataPath, expiresAt, signingKey, masterKey, signer, aud, actor, verb, scopes, consentMode } = {}) {
  if (!userId || typeof userId !== 'string') throw new Error('createJitGrant: userId required (string)');
  if (!dataPath || typeof dataPath !== 'string') throw new Error('createJitGrant: dataPath required (string)');
  // B2/B4 — external sign-only signer (shared/crypto/jit-signer.js: a
  // non-extractable WebCrypto handle the raw seed never leaves). Exclusive
  // with the raw-key forms; the async signature was reserved for exactly this.
  if (signer != null) {
    if (signingKey != null || masterKey != null) {
      throw new Error('createJitGrant: pass signer OR signingKey/masterKey, not both');
    }
    if (typeof signer.sign !== 'function'
      || !(signer.publicKey instanceof Uint8Array) || signer.publicKey.length !== 32) {
      throw new Error('createJitGrant: signer must expose sign(bytes) + 32-byte publicKey');
    }
  }
  // T568 P8 (LD-9) — optional ACTION-BOUND verb, folded into the SIGNED body
  // (presence-gated encoding block in canonicalEncodeForSigning).
  if (verb != null && !GRANT_VERBS.includes(verb)) {
    throw new Error(`createJitGrant: verb must be one of ${GRANT_VERBS.join('/')}`);
  }
  // T465 P6 (LD-53) — optional RD session scopes + consentMode (GV_V3 block).
  if (scopes != null) {
    if (!Array.isArray(scopes) || scopes.some((s) => !RD_SCOPES.includes(s))) {
      throw new Error(`createJitGrant: every scope must be one of ${RD_SCOPES.join('/')}`);
    }
  }
  if (consentMode != null && !CONSENT_MODES.includes(consentMode)) {
    throw new Error(`createJitGrant: consentMode must be one of ${CONSENT_MODES.join('/')}`);
  }

  const expiresAtIso = expiresAt || new Date(Date.now() + DEFAULT_EXPIRY_MS).toISOString();
  if (!ISO_8601_Z_RE.test(expiresAtIso)) {
    throw new Error('createJitGrant: expiresAt must be ISO-8601 UTC (Z-suffixed)');
  }

  const grantFields = {
    userId,
    dataPath,
    expiresAt: expiresAtIso,
    nonce: generateNonce(),
    jti: randomUUID(),
    maxUses: 1,
  };

  // T365 P2 (LD-4) — opt-in v2 fields. When the caller supplies aud and/or
  // actor, the grant becomes v2 and these are folded into the signed body. The
  // actor public key defaults to the grant's own signing pubkey (the "self"
  // actor — the user signing for their own decrypt); B3 will supply a distinct
  // named-agent actor keypair here for delegation.
  const isV2 = aud != null || actor != null;

  // Resolve signing key: explicit signingKey > masterKey-derived > ephemeral
  // test/mock key.
  //
  // The "ephemeral test/mock key" path: when neither signingKey nor masterKey
  // is provided, the grant is signed with a freshly-generated random Ed25519
  // keypair. This makes the grant cryptographically well-formed (signature +
  // publicKey are valid AND verify) but UN-traceable to any real user master
  // key — useful for unit tests + mock paths. Validators that inject a real
  // publicKeyLookup will reject these grants because the embedded publicKey
  // won't match the expected user identity key.
  let resolvedSigningKey = signingKey;
  let resolvedPublicKey = null;
  if (signer != null) {
    // External signer path — no raw seed in this scope at all.
    resolvedPublicKey = signer.publicKey;
  } else {
    if (!resolvedSigningKey && masterKey) {
      const kp = deriveIdentityKeypair(masterKey);
      resolvedSigningKey = kp.privateKey;
      resolvedPublicKey = kp.publicKey;
    }
    if (!resolvedSigningKey) {
      // Ephemeral key — cryptographically random, NOT derived from any master key.
      resolvedSigningKey = randomBytes(32);
    }

    if (!(resolvedSigningKey instanceof Uint8Array) || resolvedSigningKey.length !== 32) {
      throw new Error('createJitGrant: signingKey must be 32-byte Uint8Array from deriveIdentityKeypair');
    }
    // Derive public key from signing key if not already known.
    if (!resolvedPublicKey) {
      // noble's ed25519.getPublicKey takes a 32-byte seed directly.
      const { ed25519 } = require('@noble/curves/ed25519.js');
      resolvedPublicKey = ed25519.getPublicKey(resolvedSigningKey);
    }
  }

  // T365 P2 — finalize the v2 extension fields (after publicKey is known so the
  // self-actor defaults to the grant signing key). These are folded into the
  // signed canonical body via canonicalEncodeForSigning's presence-gated path.
  if (isV2) {
    grantFields.gv = GV_V2;
    grantFields.aud = aud != null ? String(aud) : '';
    const actorPk = (actor && actor.publicKey) != null
      ? String(actor.publicKey)
      : bytesToBase64(resolvedPublicKey);
    grantFields.actor = {
      publicKey: actorPk,
      label: (actor && actor.label) != null ? String(actor.label) : 'self',
    };
  }

  // T568 P8 (LD-9) — the verb rides INSIDE the signed body.
  if (verb != null) grantFields.verb = verb;

  // T465 P6 (LD-53) — canonical scope list: sorted + deduped at creation so
  // the signed encoding is deterministic regardless of caller ordering.
  if (scopes != null) grantFields.scopes = [...new Set(scopes)].sort();
  if (consentMode != null) grantFields.consentMode = consentMode;

  const msg = canonicalEncodeForSigning(grantFields);
  const sigBytes = signer != null
    ? toSigBytes(await signer.sign(msg))
    : signWithIdentity(resolvedSigningKey, msg);
  // Flat grant: all body fields at top level + signature + publicKey.
  return {
    ...grantFields,
    signature: bytesToBase64(sigBytes),
    publicKey: bytesToBase64(resolvedPublicKey),
  };
}

// Normalise an external signer's output (WebCrypto returns ArrayBuffer) to the
// 64-byte Uint8Array signature form the grant encodes.
function toSigBytes(sig) {
  const bytes = sig instanceof Uint8Array ? sig : new Uint8Array(sig);
  if (bytes.length !== 64) {
    throw new Error('createJitGrant: signer.sign must return a 64-byte Ed25519 signature');
  }
  return bytes;
}

/**
 * T365 P2 (LD-3) — canonical encoding of a presentation-time PoP challenge.
 * The actor signs THIS byte-string with their private key at presentation; the
 * validator verifies the signature against the actor public key (grant.actor.
 * publicKey, falling back to grant.publicKey for the self-actor). Domain-
 * separated from grant signing so the two signature contexts never cross.
 *
 * @param {{challenge: string, grantJti: string, ts: number|string}} c
 * @returns {Uint8Array} canonical bytes to sign/verify
 */
function encodePopChallenge(c) {
  if (!c || typeof c.challenge !== 'string' || typeof c.grantJti !== 'string' || c.ts == null) {
    throw new Error('encodePopChallenge: requires {challenge, grantJti, ts}');
  }
  const parts = [TEXT_ENCODER.encode(POP_DOMAIN_LABEL)];
  pushLengthPrefixedString(parts, c.challenge);
  pushLengthPrefixedString(parts, c.grantJti);
  pushLengthPrefixedString(parts, String(c.ts));
  return concatUint8Arrays(parts);
}

/**
 * Verify the EdDSA signature on a JIT-grant against its embedded publicKey.
 * Returns boolean (true if signature is valid).
 *
 * IMPORTANT: This verifies the signature ONLY. It does NOT check:
 *   - jti replay (validator's job)
 *   - expiresAt (validator's job)
 *   - cross-user binding (validator's job, via publicKeyLookup)
 *
 * @param {object} grant — flat grant: { userId, dataPath, expiresAt, nonce,
 *                          jti, maxUses, signature, publicKey }
 * @returns {boolean} true if signature verifies; false otherwise (non-throwing)
 */
function verifyJitGrantSignature(grant) {
  if (!grant || typeof grant !== 'object') return false;
  if (!grant.signature || !grant.publicKey) return false;
  try {
    const msg = canonicalEncodeForSigning(grant);
    const sig = base64ToBytes(grant.signature);
    const pk = base64ToBytes(grant.publicKey);
    return verifyIdentity(pk, msg, sig);
  } catch (_) {
    return false;
  }
}

/**
 * Pure schema + crypto verification of a JIT-grant (no jti tracking).
 *
 * Checks (in order):
 *   1. Grant is a non-null object
 *   2. Has all 6 required fields (userId, dataPath, expiresAt, nonce, jti, maxUses)
 *   3. jti matches a UUID format (any version — server validator is liberal)
 *   4. maxUses === 1
 *   5. expiresAt is parseable ISO-8601 UTC
 *   6. expiresAt is in the future (Date.now())
 *   7. Signature verifies against canonical encoding + embedded publicKey
 *
 * @param {object} grant — full flat grant object
 * @returns {{ok: boolean, reason?: string}}
 */
function verifyJitGrant(grant) {
  if (!grant || typeof grant !== 'object') {
    return { ok: false, reason: 'grant_not_object' };
  }
  for (const f of REQUIRED_BODY_FIELDS) {
    if (!Object.prototype.hasOwnProperty.call(grant, f)) {
      return { ok: false, reason: `grant_missing_field:${f}` };
    }
  }
  if (typeof grant.jti !== 'string' || !UUID_V4_RE.test(grant.jti)) {
    return { ok: false, reason: 'jti_not_uuid_v4' };
  }
  if (grant.maxUses !== 1) {
    return { ok: false, reason: 'maxUses_must_be_1' };
  }
  if (typeof grant.expiresAt !== 'string' || !ISO_8601_Z_RE.test(grant.expiresAt)) {
    return { ok: false, reason: 'expiresAt_not_iso8601_utc' };
  }
  const expMs = Date.parse(grant.expiresAt);
  if (Number.isNaN(expMs)) {
    return { ok: false, reason: 'expiresAt_unparseable' };
  }
  if (expMs <= Date.now()) {
    return { ok: false, reason: 'grant_expired' };
  }
  // Crypto verification (signature optional in test/mock path; if absent → fail)
  if (!verifyJitGrantSignature(grant)) {
    return { ok: false, reason: 'signature_invalid' };
  }
  return { ok: true };
}

module.exports = {
  createJitGrant,
  verifyJitGrant,
  verifyJitGrantSignature,
  canonicalEncodeForSigning,
  generateNonce,
  encodePopChallenge,
  // Constants exposed for tests + validator
  GRANT_VERBS,
  REQUIRED_BODY_FIELDS,
  UUID_V4_RE,
  ISO_8601_Z_RE,
  SIGNING_DOMAIN_LABEL,
  POP_DOMAIN_LABEL,
  DEFAULT_EXPIRY_MS,
  GV_V1,
  GV_V2,
  RD_SCOPES,
  CONSENT_MODES,
};
